/**
 * Authentication: sign in, silent refresh, sign out, and the principal every
 * screen reads for role gating.
 *
 * The refresh token is rotated on every use and lives in an `HttpOnly` cookie;
 * the portal only ever holds the 15-minute access token in memory. A refresh is
 * attempted once at boot (when this browser previously had a session) and then
 * on a timer a minute before expiry, so a long editing session never dies
 * mid-form.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { DecaApiError, unwrap, type CurrentPrincipal } from '@deca/api'

import { api, PORTAL_CLIENT_HEADER, setUnauthorizedHandler } from './api.ts'
import {
  clearSession,
  getPrincipal,
  hadSession,
  setPrincipal,
  setSession,
  subscribeSession,
} from './session.ts'
import i18n, { isLanguage } from './i18n.ts'

export type AuthStatus = 'restoring' | 'anonymous' | 'authenticated'

interface AuthContextValue {
  status: AuthStatus
  principal: CurrentPrincipal | null
  // Resolves with the signed-in principal, not void — the caller's own `principal`
  // (from `useAuth()` at the top of its component) is still whatever it was before
  // this call, because the state update that would refresh it has not re-rendered
  // yet. A caller that needs to route by role right after signing in (LoginScreen)
  // has no other way to get the *new* value synchronously with this promise.
  signIn: (email: string, password: string) => Promise<CurrentPrincipal>
  signOut: () => Promise<void>
  refreshPrincipal: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * The signed-in user's own locale is applied on every login and every silent refresh —
 * unconditionally, with no `localStorage` guard.
 *
 * Design spec §10 originally guarded this call with `hasExplicitLanguage()`: re-applying
 * the profile value on every token refresh would silently undo a choice the user had just
 * made in the UI, because back then that choice lived only in `localStorage` — the profile
 * field was an administrator's guess, set by `PATCH /users/{userId}` and by nobody else.
 * That reasoning is now superseded: `PATCH /api/v1/auth/me` makes the profile locale
 * *itself* the user's own explicit choice, written by the picker on the Profile screen, so
 * applying it here honours that choice rather than overriding it. Dropping the guard is
 * also what makes the choice travel — to a refresh, to a new device, to a browser with no
 * matching `localStorage` entry — which a client-only preference never could.
 *
 * `localStorage` (`hasExplicitLanguage`/`rememberLanguage`, `./i18n.ts`) keeps its job on
 * the surfaces that run before there is a principal to read it from — the login screen and
 * the password-reset/activation screen — where this function is never called.
 */
function applyProfileLanguage(who: CurrentPrincipal | null): void {
  if (!who) return
  if (isLanguage(who.locale)) void i18n.changeLanguage(who.locale)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() => (hadSession() ? 'restoring' : 'anonymous'))
  const [principal, setLocalPrincipal] = useState<CurrentPrincipal | null>(() => getPrincipal())

  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The scheduled callback has to reach the *current* refresh, which is defined
  // after the scheduler. A ref keeps that one-directional rather than making the
  // two callbacks depend on each other.
  const refreshRef = useRef<() => Promise<boolean>>(() => Promise.resolve(false))

  useEffect(() => subscribeSession(() => setLocalPrincipal(getPrincipal())), [])

  const cancelRefreshTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current)
      refreshTimer.current = null
    }
  }, [])

  const scheduleRefresh = useCallback(
    (expiresInSeconds: number) => {
      cancelRefreshTimer()
      const delay = Math.max(30_000, (expiresInSeconds - 60) * 1000)
      refreshTimer.current = setTimeout(() => {
        void refreshRef.current()
      }, delay)
    },
    [cancelRefreshTimer],
  )

  /**
   * Only ever one refresh in flight; a second caller awaits the first.
   *
   * The refresh token is single-use and rotated, and presenting a rotated one is — correctly
   * — read by the server as theft: it revokes the entire family and signs the user out. So
   * two concurrent refreshes do not merely duplicate work, they *destroy the session*. That
   * is not hypothetical: React's development double-invoked effects fire the boot refresh
   * twice, and in production any two queries retrying together on an expired token do the
   * same. The server log for this is
   * "refresh token reuse detected — revoking the whole chain".
   */
  const inFlightRefresh = useRef<Promise<boolean> | null>(null)

  const refresh = useCallback((): Promise<boolean> => {
    if (inFlightRefresh.current) return inFlightRefresh.current

    const attempt = (async (): Promise<boolean> => {
      try {
        const session = await unwrap(
          api.POST('/api/v1/auth/refresh', { params: { header: PORTAL_CLIENT_HEADER } }),
        )
        setSession(session.accessToken, session.expiresIn, session.principal)
        applyProfileLanguage(session.principal)
        scheduleRefresh(session.expiresIn)
        setStatus('authenticated')
        return true
      } catch {
        cancelRefreshTimer()
        clearSession()
        setStatus('anonymous')
        return false
      }
    })()

    inFlightRefresh.current = attempt
    // Cleared only if this is still the current attempt, so a refresh that starts while an
    // older one is settling is never dropped on the floor.
    void attempt.finally(() => {
      if (inFlightRefresh.current === attempt) inFlightRefresh.current = null
    })
    return attempt
  }, [cancelRefreshTimer, scheduleRefresh])

  useEffect(() => {
    refreshRef.current = refresh
  }, [refresh])

  // Boot: restore a session if this browser had one. The initial status is
  // already 'anonymous' otherwise, so there is nothing to do in that branch.
  useEffect(() => {
    if (hadSession()) void refreshRef.current()
  }, [])

  useEffect(() => {
    setUnauthorizedHandler(() => {
      cancelRefreshTimer()
      clearSession()
      setStatus('anonymous')
    })
  }, [cancelRefreshTimer])

  useEffect(() => cancelRefreshTimer, [cancelRefreshTimer])

  const signIn = useCallback(
    async (email: string, password: string) => {
      const session = await unwrap(
        api.POST('/api/v1/auth/login', { body: { email, password }, params: { header: PORTAL_CLIENT_HEADER } }),
      )
      setSession(session.accessToken, session.expiresIn, session.principal)
      applyProfileLanguage(session.principal)
      scheduleRefresh(session.expiresIn)
      setStatus('authenticated')
      return session.principal
    },
    [scheduleRefresh],
  )

  const signOut = useCallback(async () => {
    cancelRefreshTimer()
    try {
      await unwrap(api.POST('/api/v1/auth/logout', { params: { header: PORTAL_CLIENT_HEADER } }))
    } catch (error) {
      // An already-invalid session is still a successful sign-out from the
      // user's point of view; anything else is worth knowing about but must not
      // trap them in a session they asked to leave.
      if (!(error instanceof DecaApiError) || !error.isUnauthorized) {
        console.warn('logout failed', error)
      }
    }
    clearSession()
    setStatus('anonymous')
  }, [cancelRefreshTimer])

  const refreshPrincipal = useCallback(async () => {
    setPrincipal(await unwrap(api.GET('/api/v1/auth/me', {})))
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ status, principal, signIn, signOut, refreshPrincipal }),
    [status, principal, signIn, signOut, refreshPrincipal],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
